import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

import Button from "../components/Button";
import Header from "../components/Header";
import Input from "../components/Input";
import SlideViewer from "../bigComponents/slideViewer";

import { auth } from "../auth/auth";
import { getDeckById, renameDeck } from "../services/deckService";
import {
  getSlidesByDeck,
} from "../services/slideService";
import CanvasEditor from "../bigComponents/canvasEditor";

export default function Editor() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const audienceUrl = `${window.location.origin}/watch/${deckId}`;

  function handleCopyLink() {
    navigator.clipboard.writeText(audienceUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const [title, setTitle] = useState("");
  const [slides, setSlides] = useState([]);
  const [activeSlideId, setActiveSlideId] = useState(null);
  const [loadingEditorData, setLoadingEditorData] = useState(true); // <--- Renamed for clarity
  const [error, setError] = useState(null); // <--- New error state

  useEffect(() => {
    // Only proceed if user is authenticated and deckId is available
    if (!user || !deckId) {
      
        if (!authLoading && !user) { // If auth is done and no user, likely unauthenticated access
            setError("You must be logged in to view this editor.");
            setLoadingEditorData(false);
        }
        return;
        // but this piece of code is mostly for the case, which shouldnt happen as route is protected 
    }

    let isMounted = true; // Flag to prevent state updates on unmounted component

    async function loadEditor() {
      setLoadingEditorData(true); // Start loading
      setError(null); // Clear previous errors

      try {
        // 1️⃣ Load deck
        const deck = await getDeckById(deckId);
        if (!deck || deck.ownerId !== user.uid) {
          console.error("Unauthorized deck access or deck not found");
          if (isMounted) setError("Deck not found or you don't have access.");
          setLoadingEditorData(false);
          return;
        }

        if (isMounted) setTitle(deck.title);

        // 2️⃣ Load slides
        const deckSlides = await getSlidesByDeck(deckId, user.uid);

        if (!isMounted) return; // Don't update state if component unmounted

        setSlides(deckSlides);
        // Set activeSlideId, default to the first slide if available
        setActiveSlideId(deckSlides.length > 0 ? deckSlides[0].id : null);

      } catch (e) {
        console.error("Failed to load editor data:", e);
        if (isMounted) setError("Failed to load presentation. Please try again.");
      } finally {
        if (isMounted) setLoadingEditorData(false); // End loading
      }
    }

    loadEditor();

    return () => {
      isMounted = false; // Cleanup: set flag to false when component unmounts
    };
  }, [deckId, user, authLoading]); // Add authLoading to dependencies

  useEffect(() => {
    if (slides.length === 0) {
      setActiveSlideId(null);
      return;
    }

    // If current active slide was deleted, select first slide
    const stillExists = slides.some(
      (s) => s.id === activeSlideId
    );

    if (!stillExists) {
      setActiveSlideId(slides[0].id);
    }
  }, [slides, activeSlideId]);


  // Conditional rendering based on loading and error states
  if (authLoading || loadingEditorData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <p>{authLoading ? "Authenticating..." : "Loading presentation..."}</p> {/* Replace with a spinner component */}
      </div>
    );
  }

  if (error) {
      return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "red" }}>
              <p>Error: {error}</p>
          </div>
      );
  }

  if (!user) {
      // This case should ideally be caught by the error state or redirection
      // but as a fallback, if authLoading is false and no user, show a message
      return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
              <p>Please log in to access the editor.</p>
          </div>
      );
  }

  return (
    <div>
      {/* HEADER */}
      <Header centerAlign="center">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Input
            value={title}
            placeholder="Untitled Presentation"
            style={{ width: "30rem" }}
            onChange={(val) => {
              setTitle(val);
              renameDeck(deckId, val);
            }}
          />

          <div>
            <Button style={{ marginRight: "1rem" }} onClick={() => navigate(`/present/${deckId}`)}>
              Present
            </Button>
            <Button variant="outline" onClick={() => setShowAudienceModal(true)}>
              Copy Link
            </Button>
          </div>
        </div>
      </Header>

      {/* BODY */}
      <div style={{ display: "flex", height:'calc(100vh - 56px)', overflow:'hidden' }}>
        <SlideViewer
          slides={slides}
          activeSlideId={activeSlideId}
          onSelectSlide={setActiveSlideId}
          onSlidesChange={setSlides}
          deckId={deckId}
          ownerId={user.uid}
        />

        {/* MAIN CANVAS */}
         <CanvasEditor  slideId={activeSlideId}/>

      </div>

      {/* AUDIENCE LINK MODAL */}
      {showAudienceModal && (
        <div style={modalOverlay} onClick={() => setShowAudienceModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 6px", fontSize: "17px" }}>Audience Link</h3>
            <p style={{ margin: "0 0 16px", color: "#666", fontSize: "13px" }}>
              Share this link with your audience. They can view the live presentation.
            </p>
            <div style={linkRow}>
              <span style={linkText}>{audienceUrl}</span>
              <button style={copyBtn} onClick={handleCopyLink}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <button style={closeBtn} onClick={() => setShowAudienceModal(false)}>Close</button>
          </div>
        </div>
      )}

    </div>
  );
}

const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
};
const modalCard = {
  background: "#fff", borderRadius: "12px", padding: "28px 24px",
  width: "min(440px, 90vw)", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
};
const linkRow = {
  display: "flex", alignItems: "center", gap: "10px",
  background: "#f4f4f4", borderRadius: "8px", padding: "10px 14px", marginBottom: "20px",
};
const linkText = {
  flex: 1, fontSize: "13px", wordBreak: "break-all", color: "#333",
};
const copyBtn = {
  flexShrink: 0, padding: "6px 14px", background: "#3B00FF", color: "#fff",
  border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600",
};
const closeBtn = {
  width: "100%", padding: "10px", background: "transparent", border: "1px solid #ddd",
  borderRadius: "8px", cursor: "pointer", fontSize: "14px", color: "#555",
};
